// Pestaña de documentación del pedido.
//
// Aquí NO hay «documentos generados»: el servicio de generación no guarda nada
// —es sin estado, devuelve el PDF y se olvida—, así que un histórico sería
// inventado. Lo que sí es real y se muestra:
//
//   · los ADJUNTOS del pedido, contados de verdad (mismo endpoint que el visor);
//   · un único punto de generación, con la carga elegida en un buscador.
//
// El selector es un `Combobox` propio y ya no un `datalist`: aquél era buscable
// pero el navegador decidía cómo pintarlo, y no había forma de enseñar destino,
// estado y unidades dentro de la opción — que es justo lo que permite elegir sin
// salir a comprobarlo.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { pedidosVentasApi } from '@/services/api'
import { EMPRESAS_CON_PACKING } from '../CargaDocumentosDrawer'
import { fmtQty } from '../formato'
import type { ResumenCarga } from '@/utils/cargasPicking'
import type { AdjuntoDoc } from '@/types'
import { VacioDiscreto } from './controles'
import { Combobox } from './Combobox'
import {
  apagadoCls, bloqueCls, bordeTenueCls, btnNeutroCls, btnPrimarioCls,
  cifraGrandeCls, rotuloCls, tenueCls, textoCls,
} from './estilos'

interface Props {
  dark: boolean
  salesid: string
  empresa?: string
  porCarga: ResumenCarga[]
  onVerAdjuntos: () => void
  onDocumentosCarga: (carga: ResumenCarga) => void
}

export function SeccionDocumentacion({
  dark, salesid, empresa, porCarga, onVerAdjuntos, onDocumentosCarga,
}: Props) {
  const { t } = useTranslation()
  const [elegida, setElegida] = useState('')

  // Misma queryKey que el visor de adjuntos: contarlos aquí no cuesta una
  // petición extra cuando luego se abre.
  const adjuntosQ = useQuery({
    queryKey: ['venta-adjuntos', salesid],
    queryFn: () => pedidosVentasApi.getAdjuntos(salesid),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const adjuntos = (adjuntosQ.data?.data ?? []) as AdjuntoDoc[]

  const empresaConPacking = !!empresa && EMPRESAS_CON_PACKING.has(empresa.toLowerCase())

  // La primera por defecto: con una sola carga, obligar a elegir sería pedir
  // una decisión que ya está tomada.
  const carga = useMemo(
    () => porCarga.find(c => c.carga === elegida) ?? porCarga[0],
    [porCarga, elegida],
  )

  // Cada opción enseña lo que hace falta para elegir sin salir a mirarlo:
  // destino si consta, estado y unidades.
  const opciones = useMemo(() => porCarga.map(c => ({
    valor: c.carga,
    titulo: c.carga,
    detalle: [
      c.referenciaCliente,
      t(c.expedida ? 'salesOrders.picking.expedida' : 'salesOrders.picking.enAlmacen'),
      `${fmtQty(c.solicitada)} ${t('salesOrders.picking.units')}`,
    ].filter(Boolean).join(' · '),
  })), [porCarga, t])

  const dato = (etiqueta: string, valor: string) => (
    <div className="flex flex-col gap-0.5">
      <span className={rotuloCls(dark)}>{etiqueta}</span>
      <span className={`text-[14px] font-semibold tabular-nums ${textoCls(dark)}`}>{valor}</span>
    </div>
  )

  return (
    <div className="grid grid-cols-12 gap-3">
      {/* Adjuntos del pedido */}
      <section className={`col-span-12 md:col-span-4 flex flex-col gap-2.5 ${bloqueCls(dark)}`}>
        <span className={rotuloCls(dark)}>{t('attachments.title')}</span>
        <span className={cifraGrandeCls(dark)}>
          {adjuntosQ.isLoading ? '—' : adjuntos.length}
        </span>
        <p className={`text-[11px] ${apagadoCls(dark)}`}>
          {adjuntosQ.isError ? t('common.error') : t('salesOrders.detalle.adjuntosNota')}
        </p>
        <button
          type="button"
          onClick={onVerAdjuntos}
          disabled={adjuntos.length === 0}
          className={`${btnNeutroCls(dark)} self-start mt-auto disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {t('salesOrders.btn.viewDocs')}
        </button>
      </section>

      {/* Generación documental: un solo punto, con la carga elegida */}
      <section className={`col-span-12 md:col-span-8 flex flex-col gap-4 ${bloqueCls(dark)}`}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={rotuloCls(dark)}>{t('salesOrders.detalle.generarPorCarga')}</span>
          <span className={`text-[11px] ${tenueCls(dark)}`}>
            · {t('salesOrders.detalle.generarPorCargaNota')}
          </span>
        </div>

        {porCarga.length === 0 ? (
          <VacioDiscreto dark={dark} texto={t('salesOrders.nuevaCarga.sinCargas')} />
        ) : !empresaConPacking ? (
          <VacioDiscreto dark={dark} texto={t('salesOrders.documentos.noDisponibleEmpresa')} />
        ) : (
          <>
            <Combobox
              dark={dark}
              opciones={opciones}
              valor={carga?.carga ?? ''}
              onChange={setElegida}
              etiqueta={t('salesOrders.detalle.cargasCol')}
              placeholder={t('salesOrders.detalle.buscarCarga')}
            />

            {carga ? (
              <div className={`flex flex-col gap-3 pt-3 border-t ${bordeTenueCls(dark)}`}>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className={`font-mono text-[17px] font-bold ${dark ? 'text-slate-50' : 'text-slate-900'}`}>
                    {carga.carga}
                  </span>
                  {carga.referenciaCliente && (
                    <span className={`text-[13px] font-semibold ${textoCls(dark)}`}>
                      {carga.referenciaCliente}
                    </span>
                  )}
                  <span className={`text-[12px] ${
                    carga.expedida
                      ? apagadoCls(dark)
                      : dark ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    {t(carga.expedida ? 'salesOrders.picking.expedida' : 'salesOrders.picking.enAlmacen')}
                  </span>
                  {carga.aerea && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      dark ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-50 text-violet-700'
                    }`}>
                      {t('salesOrders.picking.aerea')}
                    </span>
                  )}
                </div>

                <div className="flex items-end gap-8 flex-wrap">
                  {dato(t('salesOrders.detalle.articulos'), String(carga.articulos))}
                  {dato(t('salesOrders.picking.units'), fmtQty(carga.solicitada))}
                  <button
                    type="button"
                    onClick={() => onDocumentosCarga(carga)}
                    title={t('salesOrders.documentos.abrir', { carga: carga.carga })}
                    className={`ml-auto ${btnPrimarioCls}`}
                  >
                    {t('salesOrders.detalle.generarDocumentos')}
                  </button>
                </div>
              </div>
            ) : (
              <VacioDiscreto dark={dark} texto={t('salesOrders.detalle.sinCargasFiltro')} />
            )}
          </>
        )}
      </section>
    </div>
  )
}
